import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Checkbox,
  ListItemText,
  Tabs,
  Tab,
  Badge,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useAppSelector } from '../hooks/useAppSelector';
import { useAppDispatch } from '../hooks/useAppDispatch';
import kidsClubService from '../api/services/kidsClubService';
import memberService from '../api/services/memberService';
import axiosInstance from '../api/axiosConfig';
import {
  fetchMyBookingsStart,
  fetchMyBookingsSuccess,
  fetchMyBookingsFailure,
  createBookingStart,
  createBookingSuccess,
  createBookingFailure,
  cancelBookingStart,
  cancelBookingSuccess,
  cancelBookingFailure,
  fetchCapacityStart,
  fetchCapacitySuccess,
  fetchCapacityFailure,
  clearBookingError,
  clearBookingSuccess,
} from '../store/slices/kidsClubSlice';
import { FamilyMember, KidsClubBookingFormData, BookingStatus } from '../types';
import { getErrorMessage } from '../api/axiosConfig';
import MemberCapacityCalendar from '../components/KidsClub/MemberCapacityCalendar';
import QuickDateFilters, { ViewMode } from '../components/KidsClub/QuickDateFilters';

const KidsClubBookingPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { myBookings, availableCapacity, bookingLoading, bookingError, bookingSuccess } =
    useAppSelector((state) => state.kidsClub);
  const { user } = useAppSelector((state) => state.auth);

  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [loadingFamily, setLoadingFamily] = useState(false);
  const [openBookingDialog, setOpenBookingDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [selectedCancelBookingId, setSelectedCancelBookingId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [tabValue, setTabValue] = useState(0);

  // Calendar state
  const [currentView, setCurrentView] = useState<ViewMode>('week');
  const [baseDate, setBaseDate] = useState<Date>(new Date());
  const [calendarData, setCalendarData] = useState<any[]>([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);

  // Emergency alerts state
  const [unresolvedAlerts, setUnresolvedAlerts] = useState<any[]>([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);

  const [bookingForm, setBookingForm] = useState<any>({
    dependent_ids: [], // Changed to array for multi-select
    booking_date: '',
    start_time: '08:00',
    end_time: '12:30',
    session: 'AM', // Default to Morning
    parent_location: '',
    special_instructions: '',
  });

  useEffect(() => {
    loadMyBookings();
    loadFamilyMembers();
    loadAvailableCapacity();
    loadCalendarData();
  }, []);

  useEffect(() => {
    if (familyMembers.length > 0) {
      loadUnresolvedAlerts();
    }
  }, [familyMembers]);

  useEffect(() => {
    if (bookingSuccess) {
      setOpenBookingDialog(false);
      resetBookingForm();
      setTimeout(() => dispatch(clearBookingSuccess()), 3000);
    }
  }, [bookingSuccess]);

  const loadMyBookings = async () => {
    dispatch(fetchMyBookingsStart());
    try {
      const bookings = await kidsClubService.getMyBookings();
      dispatch(fetchMyBookingsSuccess(bookings));
    } catch (error) {
      dispatch(fetchMyBookingsFailure(getErrorMessage(error)));
    }
  };

  const calculateAge = (dateOfBirth: string): number | null => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const loadFamilyMembers = async () => {
    setLoadingFamily(true);
    try {
      // Get linked members (dependents) instead of family_members table
      const response = await axiosInstance.get('/members/me/linked', {
        params: { include_dependents: true }
      });
      const members = response.data.data || response.data;

      console.log('Raw family members from API:', members);
      console.log('Number of family members:', members.length);

      // Filter for kids (dependents) who are eligible for Kids Club (ages 3-12)
      const eligibleKids = members.filter((m: any) => {
        console.log('Checking member:', {
          name: `${m.first_name} ${m.last_name}`,
          member_type: m.member_type,
          date_of_birth: m.date_of_birth,
          id: m.id
        });

        // Check if it's a dependent by member_type (from new Member hierarchy)
        const isDependent = m.member_type === 'DEPENDENT';

        if (!isDependent) {
          console.log(`Skipping ${m.first_name} - not a DEPENDENT (type: ${m.member_type})`);
          return false;
        }

        // Calculate age and check Kids Club eligibility (3-12 years old)
        const age = calculateAge(m.date_of_birth);
        if (age === null) {
          console.warn(`No date of birth for dependent: ${m.first_name} ${m.last_name} - including anyway`);
          // Include children without DOB - staff can verify age at check-in
          return true;
        }

        const isEligibleAge = age >= 3 && age <= 12;

        console.log(`✓ DEPENDENT: ${m.first_name} ${m.last_name}, Age: ${age}, Eligible: ${isEligibleAge}`);

        return isEligibleAge;
      });

      console.log(`Found ${eligibleKids.length} eligible children for Kids Club`);
      setFamilyMembers(eligibleKids);
    } catch (error) {
      console.error('Failed to load family members:', error);
    } finally {
      setLoadingFamily(false);
    }
  };

  const loadAvailableCapacity = async () => {
    dispatch(fetchCapacityStart());
    try {
      const today = new Date().toISOString().split('T')[0];
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 14); // Next 2 weeks
      const capacity = await kidsClubService.getCapacityRange(
        today,
        endDate.toISOString().split('T')[0]
      );
      dispatch(fetchCapacitySuccess(capacity));
    } catch (error) {
      dispatch(fetchCapacityFailure(getErrorMessage(error)));
    }
  };

  const loadCalendarData = async (view?: ViewMode, date?: Date) => {
    setCalendarLoading(true);
    setCalendarError(null);
    try {
      const targetView = view || currentView;
      const targetDate = date || baseDate;

      let startDate: Date;
      let endDate: Date;

      if (targetView === 'day') {
        startDate = new Date(targetDate);
        endDate = new Date(targetDate);
      } else if (targetView === 'week') {
        startDate = new Date(targetDate);
        startDate.setDate(startDate.getDate() - startDate.getDay()); // Start of week (Sunday)
        endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 6); // End of week (Saturday)
      } else {
        // month
        startDate = new Date(targetDate.getFullYear(), targetDate.getMonth(), 1);
        endDate = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0);
      }

      const data = await kidsClubService.getMemberCapacityCalendar(
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0]
      );
      setCalendarData(data.calendar || []);
    } catch (error) {
      console.error('Failed to load calendar data:', error);
      setCalendarError(getErrorMessage(error));
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleViewChange = (view: ViewMode) => {
    setCurrentView(view);
    loadCalendarData(view, baseDate);
  };

  const handleNavigate = (direction: 'prev' | 'next') => {
    const newDate = new Date(baseDate);

    if (currentView === 'day') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    } else if (currentView === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else {
      // month
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    }

    setBaseDate(newDate);
    loadCalendarData(currentView, newDate);
  };

  const loadUnresolvedAlerts = async () => {
    setLoadingAlerts(true);
    try {
      // Load alerts for member's dependents
      const alerts = await kidsClubService.getUnresolvedAlerts();
      // Filter for member's own children
      const myDependentIds = familyMembers.map(f => f.id);
      const myAlerts = alerts.filter((alert: any) => {
        // Check if alert's booking is for one of my dependents
        return alert.booking && myDependentIds.includes(alert.booking.dependent_id);
      });
      setUnresolvedAlerts(myAlerts);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    } finally {
      setLoadingAlerts(false);
    }
  };

  const handleDateClick = (date: string) => {
    // Open booking dialog and pre-fill with selected date
    setOpenBookingDialog(true);
    setBookingForm({
      ...bookingForm,
      booking_date: date,
      session: 'AM',
      start_time: '08:00',
      end_time: '12:30',
    });
  };

  const handleOpenBookingDialog = () => {
    setOpenBookingDialog(true);
    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setBookingForm({
      ...bookingForm,
      booking_date: tomorrow.toISOString().split('T')[0],
      session: 'AM',
      start_time: '08:00',
      end_time: '12:30',
    });
  };

  const handleCloseBookingDialog = () => {
    setOpenBookingDialog(false);
    resetBookingForm();
  };

  const resetBookingForm = () => {
    setBookingForm({
      dependent_ids: [], // Reset to empty array
      booking_date: '',
      start_time: '08:00',
      end_time: '12:30',
      session: 'AM', // Reset to Morning
      parent_location: '',
      special_instructions: '',
    });
  };

  // Handle session change and update times accordingly
  const handleSessionChange = (session: string) => {
    let start_time = '08:00';
    let end_time = '12:30';

    switch (session) {
      case 'AM':
        start_time = '08:00';
        end_time = '12:30';
        break;
      case 'PM':
        start_time = '12:30';
        end_time = '17:00';
        break;
      case 'EVENING':
        start_time = '17:00';
        end_time = '21:00';
        break;
    }

    setBookingForm({
      ...bookingForm,
      session,
      start_time,
      end_time,
    });
  };

  const handleCreateBooking = async () => {
    if (!bookingForm.dependent_ids || bookingForm.dependent_ids.length === 0 || !bookingForm.booking_date) {
      return;
    }

    dispatch(createBookingStart());
    try {
      // Create bookings for all selected children SEQUENTIALLY (not in parallel)
      // to avoid database session conflicts
      for (const dependentId of bookingForm.dependent_ids) {
        const bookingData = {
          dependent_id: dependentId,  // Send as UUID string
          booking_date: bookingForm.booking_date,
          start_time: bookingForm.start_time,
          end_time: bookingForm.end_time,
          parent_location: bookingForm.parent_location,
          special_instructions: bookingForm.special_instructions,
        };
        console.log('Creating booking with data:', bookingData);
        await kidsClubService.createBooking(bookingData);  // Wait for each one to complete
      }

      // Reload bookings to show the newly created ones
      await loadMyBookings();

      // Success - using the first booking for the action (all were successful)
      dispatch(createBookingSuccess({} as any));
    } catch (error) {
      console.error('Booking creation error:', error);
      dispatch(createBookingFailure(getErrorMessage(error)));
    }
  };

  const handleOpenCancelDialog = (bookingId: string) => {
    setSelectedCancelBookingId(bookingId);
    setOpenCancelDialog(true);
  };

  const handleCloseCancelDialog = () => {
    setOpenCancelDialog(false);
    setSelectedCancelBookingId(null);
    setCancelReason('');
  };

  const handleCancelBooking = async () => {
    if (!selectedCancelBookingId) return;

    dispatch(cancelBookingStart());
    try {
      const updatedBooking = await kidsClubService.cancelBooking(
        selectedCancelBookingId,
        cancelReason
      );
      dispatch(cancelBookingSuccess(updatedBooking));
      handleCloseCancelDialog();
    } catch (error) {
      dispatch(cancelBookingFailure(getErrorMessage(error)));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'checked_in':
        return 'info';
      case 'completed':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Filter bookings for upcoming (not cancelled/completed)
  const upcomingBookings = myBookings.filter(
    (b) =>
      b.status !== 'cancelled' &&
      b.status !== 'completed' &&
      new Date(b.booking_date) >= new Date(new Date().setHours(0, 0, 0, 0))
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              <ChildCareIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Kids Club Booking
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Book and manage your Kids Club reservations
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenBookingDialog}
            disabled={familyMembers.length === 0}
          >
            New Booking
          </Button>
        </Box>

        {bookingError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearBookingError())}>
            {bookingError}
          </Alert>
        )}

        {bookingSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => dispatch(clearBookingSuccess())}>
            Booking(s) created successfully!
          </Alert>
        )}

        {familyMembers.length === 0 && !loadingFamily && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No eligible children found. Please add children to your family profile first.
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarTodayIcon fontSize="small" />
                  Booking Calendar
                </Box>
              }
            />
            <Tab
              label={
                <Badge badgeContent={upcomingBookings.length} color="primary">
                  Current Bookings
                </Badge>
              }
            />
            <Tab
              label={
                <Badge badgeContent={unresolvedAlerts.length} color="error">
                  Emergency Alerts
                </Badge>
              }
            />
          </Tabs>
        </Box>

        {/* Tab 0: Booking Calendar */}
        {tabValue === 0 && (
          <Box>
            {/* Quick Date Filters */}
            <Box sx={{ mb: 3 }}>
              <QuickDateFilters
                currentView={currentView}
                onViewChange={handleViewChange}
                onNavigate={handleNavigate}
                showNavigation={true}
              />
            </Box>

            {/* Error Alert */}
            {calendarError && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setCalendarError(null)}>
                {calendarError}
              </Alert>
            )}

            {/* Loading State */}
            {calendarLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            )}

            {/* Calendar View */}
            {!calendarLoading && (
              <MemberCapacityCalendar
                calendarData={calendarData}
                loading={false}
                error={null}
                onDateClick={handleDateClick}
                isMonthView={currentView === 'month'}
              />
            )}
          </Box>
        )}

        {/* Tab 1: Current Bookings */}
        {tabValue === 1 && (
          <Grid container spacing={3}>
          {/* Upcoming Bookings */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Upcoming Bookings
                </Typography>
                {upcomingBookings.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No upcoming bookings. Create one to get started!
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Child</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Time</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Parent Location</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {upcomingBookings.map((booking) => {
                          const child = familyMembers.find((f) => f.id === booking.dependent_id);
                          return (
                            <TableRow key={booking.id}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PersonIcon fontSize="small" />
                                  {child ? `${child.first_name} ${child.last_name}` : 'Unknown'}
                                </Box>
                              </TableCell>
                              <TableCell>{formatDate(booking.booking_date)}</TableCell>
                              <TableCell>
                                {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={booking.status.replace('_', ' ').toUpperCase()}
                                  color={getStatusColor(booking.status) as any}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{booking.parent_location || 'N/A'}</TableCell>
                              <TableCell align="right">
                                {booking.status === 'confirmed' || booking.status === 'pending' ? (
                                  <Tooltip title="Cancel Booking">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleOpenCancelDialog(booking.id)}
                                    >
                                      <CancelIcon />
                                    </IconButton>
                                  </Tooltip>
                                ) : null}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
          </Grid>
        )}

        {/* Tab 2: Emergency Alerts */}
        {tabValue === 2 && (
          <Box>
            {loadingAlerts && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            )}

            {!loadingAlerts && unresolvedAlerts.length === 0 && (
              <Alert severity="success">
                No active emergency alerts for your children.
              </Alert>
            )}

            {!loadingAlerts && unresolvedAlerts.length > 0 && (
              <Stack spacing={2}>
                {unresolvedAlerts.map((alert: any) => {
                  const child = familyMembers.find(f => f.id === alert.booking?.dependent_id);
                  return (
                    <Card key={alert.id} sx={{ borderLeft: `4px solid ${alert.severity === 'HIGH' ? '#f44336' : alert.severity === 'MEDIUM' ? '#ff9800' : '#2196f3'}` }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box>
                            <Typography variant="h6" gutterBottom>
                              <NotificationsActiveIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                              {alert.alert_type.replace('_', ' ')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Child: {child ? `${child.first_name} ${child.last_name}` : 'Unknown'}
                            </Typography>
                          </Box>
                          <Chip
                            label={alert.severity}
                            color={alert.severity === 'HIGH' ? 'error' : alert.severity === 'MEDIUM' ? 'warning' : 'info'}
                            size="small"
                          />
                        </Box>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          {alert.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Sent: {new Date(alert.sent_at).toLocaleString()}
                        </Typography>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Box>
        )}

        {/* New Booking Dialog */}
        <Dialog open={openBookingDialog} onClose={handleCloseBookingDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Booking</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Select Children</InputLabel>
                <Select
                  multiple
                  value={bookingForm.dependent_ids}
                  label="Select Children"
                  onChange={(e) =>
                    setBookingForm({ ...bookingForm, dependent_ids: e.target.value })
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => {
                        const child = familyMembers.find((m) => m.id === value);
                        return (
                          <Chip
                            key={value}
                            label={child ? `${child.first_name} ${child.last_name}` : value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {familyMembers.map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      <Checkbox checked={bookingForm.dependent_ids.indexOf(member.id) > -1} />
                      <ListItemText primary={`${member.first_name} ${member.last_name}`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Booking Date"
                type="date"
                fullWidth
                value={bookingForm.booking_date}
                onChange={(e) => setBookingForm({ ...bookingForm, booking_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: new Date(new Date().setDate(new Date().getDate() + 1))
                    .toISOString()
                    .split('T')[0],
                }}
              />

              <FormControl fullWidth>
                <InputLabel>Session</InputLabel>
                <Select
                  value={bookingForm.session || 'AM'}
                  label="Session"
                  onChange={(e) => handleSessionChange(e.target.value)}
                >
                  <MenuItem value="AM">Morning</MenuItem>
                  <MenuItem value="PM">Afternoon</MenuItem>
                  <MenuItem value="EVENING">Evening</MenuItem>
                </Select>
              </FormControl>

              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    label="Start Time"
                    type="time"
                    fullWidth
                    value={bookingForm.start_time}
                    onChange={(e) =>
                      setBookingForm({ ...bookingForm, start_time: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    label="End Time"
                    type="time"
                    fullWidth
                    value={bookingForm.end_time}
                    onChange={(e) =>
                      setBookingForm({ ...bookingForm, end_time: e.target.value })
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              <TextField
                label="Where will you be at the club?"
                placeholder="e.g., Tennis Courts, Golf Course, Dining Room"
                fullWidth
                value={bookingForm.parent_location}
                onChange={(e) =>
                  setBookingForm({ ...bookingForm, parent_location: e.target.value })
                }
              />

              <TextField
                label="Special Instructions"
                multiline
                rows={3}
                fullWidth
                value={bookingForm.special_instructions}
                onChange={(e) =>
                  setBookingForm({ ...bookingForm, special_instructions: e.target.value })
                }
                placeholder="Any special instructions for the staff..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBookingDialog}>Cancel</Button>
            <Button
              onClick={handleCreateBooking}
              variant="contained"
              disabled={
                bookingLoading ||
                !bookingForm.dependent_ids ||
                bookingForm.dependent_ids.length === 0 ||
                !bookingForm.booking_date
              }
              startIcon={bookingLoading ? <CircularProgress size={20} /> : null}
            >
              {bookingLoading
                ? 'Creating...'
                : `Create Booking${bookingForm.dependent_ids.length > 1 ? 's' : ''}`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Booking Dialog */}
        <Dialog open={openCancelDialog} onClose={handleCloseCancelDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Cancel Booking</DialogTitle>
          <DialogContent>
            <Typography variant="body2" paragraph>
              Are you sure you want to cancel this booking?
            </Typography>
            <TextField
              label="Reason for Cancellation (Optional)"
              multiline
              rows={3}
              fullWidth
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCancelDialog}>Keep Booking</Button>
            <Button
              onClick={handleCancelBooking}
              color="error"
              variant="contained"
              disabled={bookingLoading}
              startIcon={bookingLoading ? <CircularProgress size={20} /> : null}
            >
              {bookingLoading ? 'Cancelling...' : 'Cancel Booking'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default KidsClubBookingPage;
